import React, { useRef, useState, useEffect, useCallback, useMemo } from "react";
import { AnimatePresence } from "framer-motion";
import FilePreview from "@/components/file/Preview";
import { FileCarousel } from "@/components/file/Carousel/FileCarousel";
import { AnnotationEditRequestDetails } from "@utils/annotationStrategies/types";
import { BoundingBox, BoundingBoxStrategyImpl } from "@utils/annotationStrategies/BoundingBox";
import AnnotationLabelInput from "@/components/annotation/AnnotationLabelInput";
import { useMedia } from "@/contexts/MediaContext";
import { useCoreApp } from "@/contexts/CoreAppContext";
import { useFlow } from "@/contexts/FlowContext";
import { useFrame } from "@/contexts/FrameContext";
import { useComputedValues } from "@/contexts/ComputedValuesContext";
import { FilePreviewProvider } from "@/contexts/FilePreviewProvider";
import { Annotation } from "@/types/annotation";
import { FloatingHint } from "@/components/FloatingHint";
import { BuildSteps } from "@/types/flows";
import { useFlowState } from "@/hooks/useFlowState";
import { createPortal } from "react-dom";
import { useGetFileCarouselHintPortalElement } from "@/components/file/useCarousel";
import { useCurrentMedia, useTextPromptMode } from "@/store";
import { useSliderHint } from "@/hooks/useSliderHint";

export const PromptVisualAreaContent = () => {
    const { addMediaItems, orderedMediaItems } = useMedia();
    const currentMedia = useCurrentMedia();
    const { showLabels, setPreviouslySelectedObject } = useCoreApp();
    const { setCurrentFrameAnnotations } = useFlow();
    const {
        selectedAnnotationId,
        setSelectedAnnotationId,
        shouldShowAnnotationInput,
        setShouldShowAnnotationInput
    } = useFrame();
    const { currentFrameAnnotations, currentFramePredictions, allAnnotationsAcrossFiles } =
        useComputedValues();
    const textPromptMode = useTextPromptMode();
    const { step } = useFlowState();
    const fileCarouselHintPortalElement = useGetFileCarouselHintPortalElement();
    const { showFloatingHint, triggerSliderHint } = useSliderHint();

    const visualAreaContainerRef = useRef<HTMLDivElement>(null);
    const [editingAnnotationDetails, setEditingAnnotationDetails] =
        useState<AnnotationEditRequestDetails<BoundingBox> | null>(null);
    // Determine if we're in review predictions mode or text prompt mode
    const isReviewingPredictions = useMemo(() => {
        return step === BuildSteps.PROMPT && (currentFramePredictions.length > 0 || textPromptMode);
    }, [step, currentFramePredictions, textPromptMode]);

    // Get unique class labels from predictions
    const uniqueClassCount = useMemo(() => {
        const uniqueClasses = new Set(currentFramePredictions.map((p) => p.class));
        return uniqueClasses.size;
    }, [currentFramePredictions]);

    // Check if current file has any annotations
    const hasAnnotationsForCurrentFile = useMemo(() => {
        if (!currentMedia?.file?.id) return false;
        const annotationsForFile = (
            Array.isArray(allAnnotationsAcrossFiles) ? allAnnotationsAcrossFiles : []
        ).filter((anno) => anno?.fileId === currentMedia.file.id && !anno.empty && !anno.draft);
        return annotationsForFile.length > 0;
    }, [allAnnotationsAcrossFiles, currentMedia?.file?.id]);

    // Generate hint message based on number of classes and annotations
    const hintMessage = useMemo(() => {
        if (!hasAnnotationsForCurrentFile) {
            return 'Click "Find Objects" to locate the objects in this file.';
        }
        const sliderText = uniqueClassCount > 1 ? "sliders" : "slider";
        return `Adjust the ${sliderText} on the right to show more or fewer boxes.`;
    }, [uniqueClassCount, hasAnnotationsForCurrentFile]);

    const onFileChange = useCallback(() => {
        setEditingAnnotationDetails(null); // Clear editing state when file changes
        // Don't reset dimensions here - let FilePreview handle them through onGetFileDimensions
    }, []);

    const handleAnnotationEditRequest = useCallback(
        (details: AnnotationEditRequestDetails<BoundingBox>) => {
            setEditingAnnotationDetails(details);
        },
        []
    );
    const handleLabelInputClose = useCallback(() => {
        setEditingAnnotationDetails(null);
        setSelectedAnnotationId(null);
        setShouldShowAnnotationInput(false);

        if (editingAnnotationDetails) {
            setCurrentFrameAnnotations((prevAnnotations: Annotation[]) =>
                (prevAnnotations || []).filter((anno: Annotation) => !anno.empty)
            );
        }
    }, [
        editingAnnotationDetails,
        setCurrentFrameAnnotations,
        setSelectedAnnotationId,
        setShouldShowAnnotationInput
    ]);

    const handleCanvasClick = useCallback(() => {
        if (isReviewingPredictions && hasAnnotationsForCurrentFile) {
            // Only trigger slider hint if there are annotations (and therefore sliders to adjust)
            triggerSliderHint();
        } else {
            // Handle label input close for annotation mode
            handleLabelInputClose();
        }
    }, [
        isReviewingPredictions,
        hasAnnotationsForCurrentFile,
        handleLabelInputClose,
        triggerSliderHint
    ]);

    const handleLabelChangedByInput = useCallback(
        (annotationId: string, newLabel: string) => {
            const sanitizedLabel = newLabel.trim().toLowerCase();
            setPreviouslySelectedObject(sanitizedLabel);
            setCurrentFrameAnnotations((prevAnnotations: Annotation[]) =>
                (prevAnnotations || []).map((anno: Annotation) =>
                    anno.id === annotationId
                        ? { ...anno, label: sanitizedLabel, draft: false, modifiedAt: Date.now() }
                        : anno
                )
            );
        },
        [setCurrentFrameAnnotations, setPreviouslySelectedObject]
    );

    const onAddMediaItems = useCallback(
        async (files: File[], onFinally?: () => void) => {
            await addMediaItems(files);

            onFinally?.();
        },
        [addMediaItems]
    );

    // Show input on label click or thumbnail click
    useEffect(() => {
        if (
            selectedAnnotationId &&
            shouldShowAnnotationInput &&
            currentMedia?.frame?.id &&
            visualAreaContainerRef.current &&
            !editingAnnotationDetails // Only trigger if not already editing (to avoid interfering with drawing flow)
        ) {
            // Find the selected annotation in the current frame
            const selectedAnnotation = currentFrameAnnotations.find(
                (anno: BoundingBox) => anno.id === selectedAnnotationId
            );

            if (selectedAnnotation) {
                // Find the actual canvas element to get its bounding rect
                const canvasElement = visualAreaContainerRef.current.querySelector(
                    "[data-annotation-canvas]"
                ) as HTMLCanvasElement;
                const canvasBoundingRect =
                    canvasElement?.getBoundingClientRect() ||
                    visualAreaContainerRef.current.getBoundingClientRect();

                // Create AnnotationEditRequestDetails for the canvas input
                const boxTlxCanvas = selectedAnnotation.x - selectedAnnotation.width / 2;
                const boxTlyCanvas = selectedAnnotation.y - selectedAnnotation.height / 2;

                const editDetails: AnnotationEditRequestDetails<BoundingBox> = {
                    annotation: selectedAnnotation,
                    boxCanvasTop: boxTlyCanvas,
                    boxCanvasLeft: boxTlxCanvas,
                    canvasBoundingRect: canvasBoundingRect
                };

                setEditingAnnotationDetails(editDetails);
            }
        }
    }, [
        selectedAnnotationId,
        shouldShowAnnotationInput,
        currentMedia?.frame?.id,
        currentFrameAnnotations,
        editingAnnotationDetails
    ]);

    return (
        <FilePreviewProvider onFileChange={onFileChange}>
            <AnimatePresence mode="wait">
                <div ref={visualAreaContainerRef} className="visualArea relative">
                    <div
                        className="flex min-h-0 flex-1"
                        onClick={() => {
                            if (isReviewingPredictions) {
                                handleCanvasClick();
                            }
                        }}
                    >
                        <FileCarousel onAddMediaItems={onAddMediaItems}>
                            <FilePreview
                                key={`file-${currentMedia?.file.id}`}
                                strategy={BoundingBoxStrategyImpl}
                                forceHideLabels={!showLabels}
                                editingAnnotationId={
                                    editingAnnotationDetails?.annotation.id || null
                                }
                                onAnnotationEditRequest={handleAnnotationEditRequest}
                                onCanvasClickWithoutAnnotation={handleCanvasClick}
                            />
                        </FileCarousel>
                    </div>

                    {editingAnnotationDetails && visualAreaContainerRef.current && (
                        <AnnotationLabelInput
                            key={`inner-input-${editingAnnotationDetails.annotation.id}`}
                            annotationEditRequestDetails={editingAnnotationDetails}
                            containerRef={visualAreaContainerRef as React.RefObject<HTMLDivElement>}
                            onLabelChange={handleLabelChangedByInput}
                            onClose={handleLabelInputClose}
                        />
                    )}

                    {/* Render the slider hint via portal */}
                    {fileCarouselHintPortalElement &&
                        showFloatingHint &&
                        createPortal(
                            <FloatingHint
                                isVisible={true}
                                message={hintMessage}
                                isDismissable={true}
                            />,
                            fileCarouselHintPortalElement
                        )}
                </div>
            </AnimatePresence>
        </FilePreviewProvider>
    );
};
